/**
 * Documentation Home Page
 *
 * Overview of documentation with links to all guides.
 */

import { Link } from 'react-router-dom';
import {
  Monitor,
  Globe,
  Cpu,
  Shield,
  BookOpen,
  ExternalLink,
  Users,
  Captions,
} from 'lucide-react';
import { useI18n, useLocalizedPath } from '@/lib/i18n';
import './docs.scss';

export function DocsHome() {
  const { t } = useI18n();
  const lp = useLocalizedPath();

  return (
    <div className="docs-content docs-home">
      <h1>{t('docs.title')}</h1>
      <p className="docs-home__subtitle">{t('docs.subtitle')}</p>

      {/* Getting Started Section */}
      <section className="docs-home__section">
        <h2>{t('docs.gettingStarted')}</h2>
        <div className="docs-home__cards">
          <Link to={lp('/docs/install/windows')} className="docs-home__card">
            <Monitor size={24} />
            <div>
              <h3>{t('install.windows')}</h3>
              <p>Install Sokuji on Windows 10/11</p>
            </div>
          </Link>

          <Link to={lp('/docs/install/macos')} className="docs-home__card">
            <Monitor size={24} />
            <div>
              <h3>{t('install.macos')}</h3>
              <p>Install Sokuji on macOS</p>
            </div>
          </Link>

          <Link to={lp('/docs/install/linux')} className="docs-home__card">
            <Monitor size={24} />
            <div>
              <h3>{t('install.linux')}</h3>
              <p>Install Sokuji on Linux</p>
            </div>
          </Link>
        </div>
      </section>

      {/* Resources Section */}
      <section className="docs-home__section">
        <h2>{t('docs.resources')}</h2>
        <div className="docs-home__cards">
          <Link to={lp('/docs/supported-sites')} className="docs-home__card">
            <Globe size={24} />
            <div>
              <h3>{t('nav.platforms')}</h3>
              <p>Google Meet, Zoom, Teams, Discord, and more</p>
            </div>
          </Link>

          <Link to={lp('/docs/ai-providers')} className="docs-home__card">
            <Cpu size={24} />
            <div>
              <h3>{t('nav.aiProviders')}</h3>
              <p>OpenAI, Google Gemini, Doubao AST 2.0, Soniox, Qwen LiveTranslate, PalabraAI</p>
            </div>
          </Link>

          <Link to={lp('/docs/features/participant-audio')} className="docs-home__card">
            <Users size={24} />
            <div>
              <h3>{t('nav.participantAudio')}</h3>
              <p>{t('docs.card.participantAudio')}</p>
            </div>
          </Link>

          <Link to={lp('/docs/features/subtitle-mode')} className="docs-home__card">
            <Captions size={24} />
            <div>
              <h3>{t('nav.subtitleMode')}</h3>
              <p>{t('docs.card.subtitleMode')}</p>
            </div>
          </Link>

          <Link to={lp('/docs/privacy')} className="docs-home__card">
            <Shield size={24} />
            <div>
              <h3>{t('nav.privacy')}</h3>
              <p>Privacy policy and data handling</p>
            </div>
          </Link>
        </div>
      </section>

      {/* External Links */}
      <section className="docs-home__section">
        <h2>Links</h2>
        <div className="docs-home__links">
          <a
            href="https://github.com/kizuna-ai-lab/sokuji"
            target="_blank"
            rel="noopener noreferrer"
            className="docs-home__link"
          >
            <BookOpen size={20} />
            GitHub Repository
            <ExternalLink size={14} />
          </a>

          <a
            href="https://chromewebstore.google.com/detail/sokuji-extension/ppmihnhelgfpjomhjhpecobloelicnak"
            target="_blank"
            rel="noopener noreferrer"
            className="docs-home__link"
          >
            <Globe size={20} />
            Chrome Web Store
            <ExternalLink size={14} />
          </a>

          <a
            href="https://microsoftedge.microsoft.com/addons/detail/sokuji-aipowered-live-/dcmmcdkeibkalgdjlahlembodjhijhkm"
            target="_blank"
            rel="noopener noreferrer"
            className="docs-home__link"
          >
            <Globe size={20} />
            Edge Add-ons
            <ExternalLink size={14} />
          </a>
        </div>
      </section>
    </div>
  );
}
